import { FC, useRef, useState } from 'react';
import styled from '@emotion/styled';
import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';
const Container = styled.div<{
  maxLines: number;
  lineHeight?: number;
  isShow: boolean;
}>`
  max-width: 100%;
  ${({
  maxLines,
  lineHeight
}) => !!lineHeight && `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box !important;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${maxLines};
    max-height: ${lineHeight * maxLines}px;
  `}
  opacity: ${({
  isShow
}) => isShow ? 1 : 0};
`;
interface IResizingText extends React.HTMLAttributes<HTMLDivElement> {
  minFontSize: number;
  maxLines?: number;
}
function getComputedLineHeight(el: Element): number {
  const style = getComputedStyle(el);
  const fs = parseFloat(style.fontSize);
  const lh = style.lineHeight;
  /**
   * 데스크톱 브라우저의 기본 line-height는 normal이고 normal은 1.2배 비율로 처리됨
   * Depends on the user agent. Desktop browsers (including Firefox)
   * use a default value of roughly 1.2, depending on the element's font-family.
   * https://developer.mozilla.org/en-US/docs/Web/CSS/line-height#normal
   */
  if (lh === 'normal') return fs * 1.2;
  if (lh.endsWith('%')) return fs * (parseFloat(lh) / 100);
  return parseFloat(lh);
}

/**
 * maxLines 초과 시 minFontSize까지 font-size를 줄여보고,
 * 그래도 넘치면 ellipsis를 적용하는 컴포넌트.
 *
 * 사용처에서 styled(ResizingText)로 font-size와 line-height를 지정하면
 * 해당 font-size를 시작점으로 minFontSize까지 축소를 시도한다.
 */
const ResizingText: FC<IResizingText> = ({
  minFontSize,
  maxLines = 1,
  children,
  ...props
}) => {
  const [lineHeight, setLineHeight] = useState<number | undefined>(undefined);
  const [fontSize, setFontSize] = useState<number | undefined>(undefined);
  const [isShow, setIsShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useIsomorphicLayoutEffect(() => {
    const target = ref.current;
    if (!target) return;
    setFontSize(undefined);
    setLineHeight(undefined);
    setIsShow(false);
    try {
      const observerCallback: ResizeObserverCallback = entries => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const el = entries[0].target as HTMLElement;
          const computedLineHeight = getComputedLineHeight(el);
          const containerWidth = target.getBoundingClientRect().width;
          if (el.scrollHeight / computedLineHeight <= maxLines && entries[0].contentRect.width <= containerWidth && el.scrollWidth <= el.clientWidth) {
            // text가 container의 영역을 넘어가지 않는 경우에도
            // lineHeight를 설정하여 Container의 overflow/clamp CSS가 적용되도록 한다.
            setLineHeight(computedLineHeight);
            return setIsShow(true);
          }

          // scrollHeight를 사용하여 font-size를 동기적으로 줄여나간다.
          let currentFontSize = parseFloat(getComputedStyle(el).fontSize);
          while (currentFontSize > minFontSize) {
            currentFontSize -= 1;
            el.style.fontSize = `${currentFontSize}px`;
            const lh = getComputedLineHeight(el);
            if (el.scrollHeight / lh <= maxLines && el.scrollWidth <= el.clientWidth) {
              setFontSize(currentFontSize);
              setLineHeight(lh);
              setIsShow(true);
              return;
            }
          }

          // minFontSize까지 줄여도 넘치면 ellipsis로 처리
          const finalLh = getComputedLineHeight(el);
          setFontSize(minFontSize);
          setLineHeight(finalLh);
          setIsShow(true);
        });
      };
      const observer = new ResizeObserver(observerCallback);
      observer.observe(target);
      return () => {
        observer.disconnect();
      };
    } catch (e) {
      // sentry error 집계 무시
      // ref: https://stackoverflow.com/a/50387233
    }
  }, [minFontSize, maxLines, children]);
  return <Container ref={ref} lineHeight={lineHeight} maxLines={maxLines} style={{
    fontSize
  }} isShow={isShow} {...props} data-sentry-element="Container" data-sentry-component="ResizingText" data-sentry-source-file="index.tsx">
      {children}
    </Container>;
};
export default ResizingText;